import { CheckIcon } from '@chakra-ui/icons'
import {
  Box,
  Button,
  Center,
  Checkbox,
  CircularProgress,
  Divider,
  Flex,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useCollectionItem, usePopUpMessage } from '@hb/react'
import { ErrorWithMessage, midwivesCollection, MissingConsentForm } from '@hb/shared'
import { useCallback, useState } from 'react'
import { sendConsentForms } from '../../../../backend/functions/consentForms'
import { useMissingForms } from '../../../../contexts/MissingFormsContext'
import { useProfile } from '../../../../contexts/user/ProfileContext'
import { ActionButton, SolidActionButton } from '../../../Buttons'
import { DefaultModal } from '../../../Modals/DefaultModal'
import { AutoDisplayConditionsPopover } from '../../../Templates/ConsentForms/AutoDisplayCondition'

export const MissingFormView = ({
  missingForm,
  selected,
  onSelect,
  submitted,
  submitting,
}: {
  missingForm: MissingConsentForm
  selected: boolean
  submitted: boolean
  submitting: boolean
  onSelect: (updated: boolean) => void
}) => {
  const { practiceId, form, conditions } = missingForm
  const { item: practice } = useCollectionItem(midwivesCollection, practiceId)
  const practiceName = practice?.name
  let indicator: React.ReactNode = null
  if (submitting) {
    indicator = <CircularProgress isIndeterminate color="green.600" size={5} />
  } else if (submitted) {
    indicator = <CheckIcon color="green.600" />
  } else {
    indicator = <Checkbox size="lg" colorScheme="green" isChecked={selected} pointerEvents="none" />
  }
  return (
    <Flex justifyContent="flex-start" alignItems="center" w="100%" p={2} gap={3}>
      <Button
        onClick={() => onSelect(!selected)}
        flex={1}
        gap={2}
        display="flex"
        justifyContent="flex-start"
        variant="unstyled">
        <Center w="32px">{indicator}</Center>
        <Flex alignItems="flex-start" flexFlow="column">
          <Text lineHeight={1} opacity={0.8}>
            {practiceId ? `${practiceName || 'Practice'} Consent Form` : 'HB Consent Form'}
          </Text>
          <Text fontFamily="Hero-New" fontWeight={500} fontSize="lg">
            {form.name}
          </Text>
        </Flex>
      </Button>
      <AutoDisplayConditionsPopover conditions={conditions} />
    </Flex>
  )
}

export const MissingForms = ({
  missingForms,
  subtitle,
  onClose,
  patientId,
  assessmentId,
}: {
  missingForms: MissingConsentForm[]
  patientId: string | null
  assessmentId: string | null
  subtitle?: string
  onClose?: () => void
}) => {
  const [selectedForms, setSelectedForms] = useState<MissingConsentForm[]>([])
  const [submittedForms, setSubmittedForms] = useState<MissingConsentForm[]>([])

  const onSelect = useCallback((form: MissingConsentForm, selected: boolean) => {
    setSelectedForms(prev => (selected ? [...prev, form] : prev.filter(f => f !== form)))
  }, [])

  const { showError, showSuccess } = usePopUpMessage()

  const [isSubmitting, setIsSubmitting] = useState(false)
  const handleSubmit = useCallback(async () => {
    if (!selectedForms.length) {
      return
    }
    if (!patientId) {
      showError('No patientId')
      return
    }
    if (!assessmentId) {
      showError('No assessmentId')
      return
    }
    setSubmittedForms(selectedForms)
    setIsSubmitting(true)
    try {
      await sendConsentForms({
        patientId,
        assessmentId,
        consentFormIds: selectedForms.map(form => ({
          practiceId: form.practiceId,
          templateId: form.form.id,
        })),
      })
      showSuccess('Consent forms sent')
      if (onClose) onClose()
    } catch (error) {
      console.error(error)
      showError(
        `Error sending consent forms: ${(error as ErrorWithMessage).message || 'Unknown error'}`,
      )
    } finally {
      setIsSubmitting(false)
    }
  }, [selectedForms, patientId, assessmentId, showError, showSuccess, onClose])

  return (
    <Flex w="100%" flexFlow="column">
      <Flex
        bg="gray.100"
        borderBottomWidth="1px"
        borderColor="gray.200"
        flexFlow="column"
        py={2}
        px={3}>
        <Text fontSize="lg" fontWeight={500}>
          Missing Consent Forms
        </Text>
        {subtitle ? <Text fontSize="md">{subtitle}</Text> : null}
      </Flex>
      <Box>
        <VStack spacing={0} w="100%" alignItems="flex-start" divider={<Divider />}>
          {missingForms.map(missingForm => (
            <MissingFormView
              key={missingForm.form.id}
              missingForm={missingForm}
              selected={selectedForms.includes(missingForm)}
              submitted={submittedForms.includes(missingForm)}
              submitting={isSubmitting && submittedForms.includes(missingForm)}
              onSelect={selected => onSelect(missingForm, selected)}
            />
          ))}
        </VStack>
      </Box>
      <Flex
        px={3}
        py={2}
        justifyContent="flex-end"
        gap={2}
        borderTopWidth="1px"
        bg="gray.100"
        borderColor="gray.200">
        {onClose ? <ActionButton onClick={onClose}>Close</ActionButton> : null}
        <SolidActionButton
          isLoading={isSubmitting}
          pointerEvents={selectedForms.length > 0 ? 'auto' : 'none'}
          opacity={selectedForms.length > 0 ? 1 : 0.5}
          onClick={handleSubmit}>
          Send {selectedForms.length !== 1 ? 'Forms' : 'Form'}
        </SolidActionButton>
      </Flex>
    </Flex>
  )
}

export const MissingFormsModal = () => {
  const { missingForms, setMissingForms } = useMissingForms()
  const { userId, assessmentId } = useProfile()
  return (
    <DefaultModal
      isOpen={!!missingForms.length}
      onClose={() => setMissingForms([])}
      overlayHeader
      closeDisabled
      render={() => (
        <MissingForms
          subtitle="The updated questionnaire data calls for the following consent forms:"
          missingForms={missingForms}
          onClose={() => setMissingForms([])}
          patientId={userId}
          assessmentId={assessmentId}
        />
      )}
    />
  )
}
